import { useState } from "react";
import { Link } from "react-router-dom";
import { useLocale } from "../../../intl";
import { useClientView } from "../../../clientViewRuntime/context/ClientViewProvider";
import { useLocalizedRuntimePaths } from "../../../clientViewRuntime/hooks/useLocalizedRuntimePaths";
import {
  GUEST_FINANCING_DEFAULTS,
  loadGuestFinancingPrefs,
  parseCopInput,
  saveGuestFinancingPrefs,
} from "../../../utils/automap/buyerFinancingPrefsStorage";
import { formatPriceCop } from "../../../utils/automapListing/listingFormatters";

const QUICK_SETUP_KEY_PREFIX = "automap_buyer_quick_setup:";

export function loadGuestQuickSetupContact(slug) {
  if (!slug || typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(`${QUICK_SETUP_KEY_PREFIX}${slug}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveGuestQuickSetupContact(slug, contact) {
  if (!slug || typeof window === "undefined") return;
  try {
    localStorage.setItem(`${QUICK_SETUP_KEY_PREFIX}${slug}`, JSON.stringify(contact));
  } catch {
    // ignore quota errors
  }
}

export default function BuyerPaymentQuickSetup({ onSaved, compact = true }) {
  const { slug } = useClientView();
  const { t } = useLocale();
  const { signInPath } = useLocalizedRuntimePaths();
  const savedContact = loadGuestQuickSetupContact(slug);
  const existingPrefs = loadGuestFinancingPrefs(slug) || {};

  const [form, setForm] = useState({
    email: savedContact?.email || "",
    firstName: savedContact?.firstName || "",
    downPaymentCop: existingPrefs.downPaymentCop
      ? String(Math.round(Number(existingPrefs.downPaymentCop)))
      : "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [savedMessage, setSavedMessage] = useState("");

  const handleSubmit = (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSavedMessage("");

    const downPaymentCop = parseCopInput(form.downPaymentCop);
    if (!downPaymentCop || downPaymentCop <= 0) {
      setErrorMessage(t("automotive.paymentEstimate.quickSetupDownPaymentRequired"));
      return;
    }

    if (!form.email.trim()) {
      setErrorMessage(t("automotive.paymentEstimate.quickSetupEmailRequired"));
      return;
    }

    if (!form.firstName.trim()) {
      setErrorMessage(t("automotive.paymentEstimate.quickSetupNameRequired"));
      return;
    }

    saveGuestFinancingPrefs(slug, {
      ...GUEST_FINANCING_DEFAULTS,
      ...existingPrefs,
      downPaymentCop,
      source: "quick_setup",
      updatedAt: new Date().toISOString(),
    });
    saveGuestQuickSetupContact(slug, {
      email: form.email.trim(),
      firstName: form.firstName.trim(),
    });
    setSavedMessage(t("automotive.paymentEstimate.quickSetupSaved"));
    onSaved?.();
  };

  const parsedDownPayment = parseCopInput(form.downPaymentCop);
  const registerHref = `${signInPath}?mode=register`;

  return (
    <div
      className={`automap-payment-quick-setup${compact ? " automap-payment-quick-setup--compact" : ""}`}
    >
      <p className="automap-payment-quick-setup__intro muted">
        {t("automotive.paymentEstimate.quickSetupIntro")}
      </p>
      <form className="automap-payment-quick-setup__form" onSubmit={handleSubmit}>
        <label className="automap-payment-quick-setup__field">
          <span>{t("automotive.signInPage.emailLabel")}</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
          />
        </label>
        <label className="automap-payment-quick-setup__field">
          <span>{t("automotive.signInPage.firstNameLabel")}</span>
          <input
            type="text"
            required
            autoComplete="given-name"
            value={form.firstName}
            onChange={(event) => setForm({ ...form, firstName: event.target.value })}
          />
        </label>
        <label className="automap-payment-quick-setup__field">
          <span>{t("automotive.preferences.downPaymentLabel")}</span>
          <input
            type="text"
            inputMode="numeric"
            required
            value={form.downPaymentCop}
            onChange={(event) => setForm({ ...form, downPaymentCop: event.target.value })}
            placeholder={t("automotive.preferences.downPaymentPlaceholder")}
          />
          {parsedDownPayment ? (
            <span className="muted automap-payment-quick-setup__hint">
              {formatPriceCop(parsedDownPayment)}
            </span>
          ) : null}
        </label>
        {errorMessage ? <p className="automap-payment-quick-setup__error">{errorMessage}</p> : null}
        {savedMessage ? (
          <p className="automap-payment-quick-setup__success">{savedMessage}</p>
        ) : null}
        <div className="automap-payment-quick-setup__actions">
          <button type="submit" className="btn btn-primary automap-payment-quick-setup__submit">
            {t("automotive.paymentEstimate.quickSetupSaveCta")}
          </button>
          <Link className="btn btn-soft automap-payment-quick-setup__register" to={registerHref}>
            {t("automotive.paymentEstimate.quickSetupCreateAccountCta")}
          </Link>
        </div>
      </form>
    </div>
  );
}
